import Add from "@mui/icons-material/Add";
import Delete from "@mui/icons-material/Delete";
import Button from "@mui/material/Button";
import Grid2 from "@mui/material/Grid2";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import classNames from "classnames";
import React, { useState } from "react";
import { useFieldArray } from "react-hook-form";
import { ClassificationQuerySchemaType } from "../../../schemas/OrionConfig";
import FormTextField from "../../Common/FormTextField";
import {
  useConfigModeContext,
  useFormConfigContext,
} from "./ConfigFormContext";

interface ConfigClassificationQueryProps {
  index: number;
  groupIndex?: number;
  removeQuery: Function;
}

const ConfigClassificationQuery: React.FC<ConfigClassificationQueryProps> = ({
  index,
  groupIndex,
  removeQuery,
}) => {
  const { control, setValue, watch } = useFormConfigContext();
  const { isReadOnly } = useConfigModeContext();
  const [open, setOpen] = useState<boolean>(false);

  const queryPath =
    groupIndex !== undefined
      ? (`config.classification.queries.${index}.queries.${groupIndex}` as const)
      : (`config.classification.queries.${index}` as const);

  const {
    fields: classes,
    append: addClass,
    remove: removeClass,
  } = useFieldArray({
    control,
    name: `${queryPath}.classes`,
  });

  const query = watch(`${queryPath}`) as ClassificationQuerySchemaType;

  const getContent = () => {
    if (!open) {
      return (
        <>
          <div className="flex gap-2 w-full items-center justify-between">
            <Typography fontSize={16} fontWeight={600}>
              {query.name}
            </Typography>
            <div className="flex gap-2 items-center">
              <Typography fontWeight={200}>{query.type}</Typography>
              {!isReadOnly && (
                <Button color="error" onClick={() => removeQuery()}>
                  <Delete color="error" />
                </Button>
              )}
            </div>
          </div>
          <Typography fontSize={12} className="truncate">
            {query.classes.map((classe) => classe.name).join(", ")}
            {query.default && `, (${query.default.name})`}
          </Typography>
        </>
      );
    } else {
      return (
        <Stack rowGap={4}>
          <div className="flex gap-2">
            <FormTextField
              control={control}
              name={`${queryPath}.name`}
              placeholder="Nom de la classification"
              variant="standard"
              fullWidth
              slotProps={{
                input: {
                  style: { fontSize: 20, fontWeight: 600 },
                },
              }}
            />
            <Button onClick={() => setOpen(false)}>OK</Button>
          </div>
          <div className="flex justify-between items-center">
            <Typography className="text-lg font-semibold">Classes</Typography>
            {!isReadOnly && (
              <Button
                color="info"
                onClick={() =>
                  addClass({
                    name: "",
                    description: "",
                  })
                }
              >
                <Add fontSize="small" />
              </Button>
            )}
          </div>
          {classes?.map((classe, classIndex) => (
            <Grid2
              container
              spacing={2}
              alignItems="center"
              key={`${queryPath}.classes.${classe.id}`}
            >
              <Grid2 size={3}>
                <FormTextField
                  size="small"
                  control={control}
                  name={`${queryPath}.classes.${classIndex}.name`}
                  label="Nom de la classe"
                  multiline
                />
              </Grid2>
              <Grid2 size={8}>
                <FormTextField
                  size="small"
                  control={control}
                  name={`${queryPath}.classes.${classIndex}.description`}
                  label="Description de la classe"
                  multiline
                />
              </Grid2>
              <Grid2 size={1}>
                {classes.length > 2 && (
                  <Button onClick={() => removeClass(classIndex)}>
                    <Delete color="error" />
                  </Button>
                )}
              </Grid2>
            </Grid2>
          ))}
          <div className="flex justify-between items-center">
            <Typography className="text-lg font-semibold">
              Classe par défaut
            </Typography>
            {!isReadOnly &&
              (query.default ? (
                <Button
                  onClick={() => setValue(`${queryPath}.default`, undefined)}
                >
                  <Delete color="error" />
                </Button>
              ) : (
                <Button
                  onClick={() =>
                    setValue(`${queryPath}.default`, {
                      name: "",
                    })
                  }
                  color="info"
                >
                  <Add fontSize="small" />
                </Button>
              ))}
          </div>
          {query.default ? (
            <div className="flex flex-1 gap-2">
              <FormTextField
                size="small"
                control={control}
                name={`${queryPath}.default.name`}
                label="Nom de la classe par défaut"
                className="w-1/5"
              />
              <FormTextField
                size="small"
                control={control}
                name={`${queryPath}.default.description`}
                label="Description de la classe  par défaut"
                className="w-4/5"
              />
            </div>
          ) : (
            <Typography className="text-sm italic">
              Aucune classe par défaut. En cas d'erreur ou si le modèle n'est
              pas capable de classifier le document parmis les classes données,
              la classe par défaut sera utilisée
            </Typography>
          )}
        </Stack>
      );
    }
  };

  return (
    <Paper
      square={false}
      elevation={3}
      className={classNames(
        "flex flex-col gap-2 p-2 border-[var(--mui-palette-primary-main)] border-solid	border-1",
        {
          "hover:cursor-pointer": !open,
        }
      )}
      onClick={!open ? () => setOpen(true) : undefined}
    >
      {getContent()}
    </Paper>
  );
};
export default ConfigClassificationQuery;
